// Ground-plane geometry for the layout's shapes: signed distances to regions, the nearest point of a polyline (and how
// far along it), smoothstep. Pure.
import type { Shape, Vec2 } from "./layout";

/** Hermite smoothstep from e0 to e1 (either order). */
export function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** Signed distance (m) from (x, z) to a shape's outline: negative inside. */
export function shapeDistance(s: Shape, x: number, z: number): number {
  if (s.kind === "circle") return Math.hypot(x - s.x, z - s.z) - s.r;
  if (s.kind === "rect") {
    const cx = (s.x0 + s.x1) / 2;
    const cz = (s.z0 + s.z1) / 2;
    const dx = Math.abs(x - cx) - (s.x1 - s.x0) / 2;
    const dz = Math.abs(z - cz) - (s.z1 - s.z0) / 2;
    return Math.hypot(Math.max(dx, 0), Math.max(dz, 0)) + Math.min(Math.max(dx, dz), 0);
  }
  const pts = s.points;
  let d = Infinity;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, zi] = pts[i]!;
    const [xj, zj] = pts[j]!;
    d = Math.min(d, segmentDistance(x, z, xj, zj, xi, zi).d);
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside ? -d : d;
}

/** A shape's centre (a polygon's vertex mean). */
export function shapeCentre(s: Shape): Vec2 {
  if (s.kind === "circle") return [s.x, s.z];
  if (s.kind === "rect") return [(s.x0 + s.x1) / 2, (s.z0 + s.z1) / 2];
  let x = 0;
  let z = 0;
  for (const p of s.points) {
    x += p[0];
    z += p[1];
  }
  return [x / s.points.length, z / s.points.length];
}

/** A shape's bounding rectangle. */
export function shapeBox(s: Shape): { x0: number; z0: number; x1: number; z1: number } {
  if (s.kind === "circle") return { x0: s.x - s.r, z0: s.z - s.r, x1: s.x + s.r, z1: s.z + s.r };
  if (s.kind === "rect") return { x0: s.x0, z0: s.z0, x1: s.x1, z1: s.z1 };
  const xs = s.points.map((p) => p[0]);
  const zs = s.points.map((p) => p[1]);
  return { x0: Math.min(...xs), z0: Math.min(...zs), x1: Math.max(...xs), z1: Math.max(...zs) };
}

/** Distance from (x, z) to the segment a–b, and the parameter t (0 at a, 1 at b) of the nearest point. */
export function segmentDistance(x: number, z: number, ax: number, az: number, bx: number, bz: number): { d: number; t: number } {
  const dx = bx - ax;
  const dz = bz - az;
  const l2 = dx * dx + dz * dz;
  const t = l2 > 0 ? Math.min(1, Math.max(0, ((x - ax) * dx + (z - az) * dz) / l2)) : 0;
  return { d: Math.hypot(x - (ax + dx * t), z - (az + dz * t)), t };
}

/** A polyline with its cumulative lengths, for nearest-point queries. */
export interface Polyline {
  points: readonly Vec2[];
  /** Length from the start to each point (m). */
  at: number[];
  length: number;
  box: { x0: number; z0: number; x1: number; z1: number };
}

/** Prepare a polyline. */
export function polyline(points: readonly Vec2[]): Polyline {
  const at = [0];
  for (let i = 1; i < points.length; i++) at.push(at[i - 1]! + Math.hypot(points[i]![0] - points[i - 1]![0], points[i]![1] - points[i - 1]![1]));
  const xs = points.map((p) => p[0]);
  const zs = points.map((p) => p[1]);
  return { points, at, length: at[at.length - 1]!, box: { x0: Math.min(...xs), z0: Math.min(...zs), x1: Math.max(...xs), z1: Math.max(...zs) } };
}

/** The nearest point of a polyline to (x, z): its distance and its distance along the line (m). */
export function nearestOn(p: Polyline, x: number, z: number): { d: number; s: number } {
  let best = { d: Infinity, s: 0 };
  for (let i = 1; i < p.points.length; i++) {
    const a = p.points[i - 1]!;
    const b = p.points[i]!;
    const r = segmentDistance(x, z, a[0], a[1], b[0], b[1]);
    if (r.d < best.d) best = { d: r.d, s: p.at[i - 1]! + r.t * (p.at[i]! - p.at[i - 1]!) };
  }
  return best;
}

/** The point `s` metres along a polyline (clamped to its ends). */
export function pointAlong(p: Polyline, s: number): Vec2 {
  const t = Math.min(p.length, Math.max(0, s));
  for (let i = 1; i < p.points.length; i++) {
    if (t <= p.at[i]! || i === p.points.length - 1) {
      const seg = p.at[i]! - p.at[i - 1]!;
      const k = seg > 0 ? (t - p.at[i - 1]!) / seg : 0;
      const a = p.points[i - 1]!;
      const b = p.points[i]!;
      return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
    }
  }
  return p.points[0]!;
}

/** Distance from (x, z) to a box (0 inside). */
export function boxDistance(b: { x0: number; z0: number; x1: number; z1: number }, x: number, z: number): number {
  return Math.hypot(Math.max(b.x0 - x, 0, x - b.x1), Math.max(b.z0 - z, 0, z - b.z1));
}
